import { React, useContext, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import validator from "validator";
import LogoComponent from "../../components/Logos/LogoComponent";
import SubmitButton from "../../components/buttons/SubmitButton";
import ActionButton from "../../components/buttons/ActionButton";
import injectScriptToDOM from "../../scripts/injectScriptToDOM";
import { CurrentUserContext } from "../../App";
import { useRateLimiter } from "hooks/useRateLimiter";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

// this needs to be re initialized before fetching auth endpoints
// in this case, on form validation or google sign up
// used to listen to the signal send from hooks/useLoginRateLimiter.js
export let signupController;

export default function SignUpPage() {
  // define an event to listen for in rate limiter
  const rateLimitEvent = new Event("checkLimit");
  // initialize variables
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullnameError, setFullnameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // get current user from context in App.js
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);

  const navigate = useNavigate();

  // limit the number of requests sent to 3 per minute
  useRateLimiter();

  useEffect(() => {
    // load google api
    injectScriptToDOM("https://accounts.google.com/gsi/client", true);
    // cleanup script tag if it exists
    return () => {
      document.getElementById("google-oauth2")?.remove();
    };
  }, [currentUser]);

  // Handle Google OAuth Response
  // TODO? if google email is verified skip email verification step
  window.handleCredentialResponse = (credentials) => {
    // reset controller and dispatch auth event for rate limiter
    signupController = new AbortController();
    window.dispatchEvent(rateLimitEvent);
    // this fetch listens for the abort signal sent by useRateLimiter
    fetch(`${API_URL}/g-signin`, {
      method: "POST",
      signal: signupController.signal,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(credentials),
    }).then((r) =>
      r.json().then((r) => {
        if ("success" === r.message) {
          setCurrentUser(r.user);
          localStorage.setItem("currentUser", JSON.stringify(r.user));
          if ("login" === r.mode) {
            navigate("/memories");
          } else if ("signup" === r.mode) {
            navigate("/login/emailVerification");
          }
        } else {
          alert(r.message);
        }
      })
    );
  };

  // Register a user's email and password
  const register = () => {
    // this fetch listens for the abort signal sent by useLoginRateLimiter
    fetch(`${API_URL}/register`, {
      signal: signupController.signal,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fullname, email, password }),
    })
      .then((r) => r.json())
      .then((r) => {
        setIsLoading(false);
        if ("success" === r.message) {
          setCurrentUser(r.user);
          localStorage.setItem("currentUser", JSON.stringify(r.user));
          navigate("/login/emailVerification");
        } else {
          alert(r.message);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        alert("Registration failed. Please try again.");
      });
  };

  const onFormSubmit = (e) => {
    e.preventDefault();

    // Set initial error values to empty
    setFullnameError("");
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");

    let formIsValid = true;

    // Check if the user has entered form fields correctly
    if (fullname.length < 2) {
      formIsValid = false;
      setFullnameError("Please enter your name");
    } else if (!/^[a-zA-Z\s]+$/.test(fullname)) {
      formIsValid = false;
      setFullnameError("Name must contain only letters and spaces");
    }

    if (!validator.isEmail(email)) {
      formIsValid = false;
      setEmailError("Please enter a valid email");
    }

    if (password.length < 8) {
      formIsValid = false;
      setPasswordError("The password must be 8 characters or longer");
    }

    if (password !== confirmPassword) {
      formIsValid = false;
      setConfirmPasswordError("The passwords must match");
    }

    if (
      !password.match(/[a-z]+/) ||
      !password.match(/[A-Z]+/) ||
      !password.match(/[0-9]+/) ||
      !password.match(/[!@#$%^&*]+/)
    ) {
      formIsValid = false;
      setPasswordError(
        "The password must be at least 8 characters long and include at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character (!, @, #, $, %, ^, &, *)"
      );
    }

    if (formIsValid) {
      setIsLoading(true);
      // reset controller and dispatch auth event for login rate limiter
      signupController = new AbortController();
      window.dispatchEvent(rateLimitEvent);
      // Check if email has an account associated with it
      checkAccountExists((accountExists) => {
        // If no, register account
        if (!accountExists) {
          register();
        }
        // Else, ask user if they want to create a new account and if yes, then log in
        else if (
          window.confirm(
            "An account already exists for " +
              email +
              ". Do you want to log in?"
          )
        ) {
          setIsLoading(false);
          navigate("/login");
        } else {
          setIsLoading(false);
        }
      });
    }
  };

  // Call the server API to check if the given email ID already exists
  const checkAccountExists = (callback) => {
    // this fetch listens for the abort signal sent by useLoginRateLimiter
    fetch(`${API_URL}/check-account`, {
      signal: signupController.signal,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    })
      .then((r) => r.json())
      .then((r) => {
        callback(r?.userExists);
      });
  };

  // Log the user out
  const logOut = () => {
    setCurrentUser(null);
    localStorage.removeItem("currentUser");
    navigate("/login");
    return;
  };

  return (
    // THIS CENTERS THE WHOLE THING
    <div className="d-flex flex-column min-vh-100 justify-content-center align-items-center">
      {/* CONTAINER THAT HOLDS THE STUFFF */}

      <div style={{ maxWidth: "30rem", width: "100%", padding: "0 1rem" }}>
        {/* LOGO CONTAINER */}
        <div className="mb-4">
          <LogoComponent />
        </div>

        {/* CONTAINER HOLDS THE BACK BUTTON, TEXT, FORM */}
        <div className="d-flex flex-column justify-content-center align-items-center">
          <span className="mb-3 heading-secondary text-center">
            Creating a New Experience
          </span>

          {currentUser?.email ? (
            <>
              <div className="mb-4">Logged in as {currentUser.email}</div>
              <ActionButton
                buttonText={"Log Out"}
                buttonValue={"logout"}
                buttonAction={logOut}
              />
            </>
          ) : (
            <div className="w-100">
              {/* Sign Up Form */}
              <form onSubmit={onFormSubmit} noValidate className="w-100">
                {/* Name Input */}
                <div className="mb-2">
                  {/* <label for="inputName" class="form-label">
                      Name
                    </label> */}
                  <input
                    className="form-control w-100"
                    id="inputName"
                    aria-describedby="NameHelp"
                    type="text"
                    name="name"
                    placeholder="Name"
                    value={fullname}
                    onChange={(e) => setFullname(e.target.value)}
                    required
                  />
                  <label for="inputName" className="errorLabel">
                    {fullnameError}
                  </label>
                </div>

                {/* Email Input */}
                <div className="mb-2">
                  {/* <label for="inputEmail" class="form-label">
                Email
              </label> */}
                  <input
                    className="form-control"
                    id="inputEmail"
                    type="email"
                    name="email"
                    placeholder={"Email"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-describedby="emailHelp"
                    required
                  />
                  <label for="inputEmail" className="errorLabel">
                    {emailError}
                  </label>
                </div>

                {/* Password Input*/}
                <div className="mb-1">
                  {/* <label for="inputPassword">Password</label> */}
                  <input
                    className="form-control"
                    id="inputPassword"
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <label for="inputPassword" className="errorLabel">
                    {passwordError}
                  </label>
                </div>

                {/* Confirm Password Input*/}
                <div className="mb-2">
                  {/* <label for="inputPassword">Password</label> */}
                  <input
                    className="form-control"
                    id="confirmInputPassword"
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <label for="confirmInputPassword" className="errorLabel">
                    {confirmPasswordError}
                  </label>
                </div>

                {/* Sign Up Button */}
                <div className="mb-4">
                  <SubmitButton
                    buttonText={"Sign Up"}
                    buttonValue={"register"}
                    loading={isLoading}
                  />
                </div>
              </form>

              <div className="d-flex flex-column gap-2 text-center mb-4 w-100">
                {/* OR TEXT */}
                <div className="d-flex align-items-center gap-3 mb-4">
                  <hr className="flex-grow-1 m-0" style={{ borderColor: "white" }} />
                  <span style={{ fontSize: "1.2rem" }}>OR</span>
                  <hr className="flex-grow-1 m-0" style={{ borderColor: "white" }} />
                </div>

                {/* GOOGLE SIGN UP CONTAINER */}
                <div className="d-flex flex-column mb-4 w-100 align-items-center">
                  {/* Google Sign Up Button */}

                  <div
                    className="w-100"
                    id="g_id_onload"
                    data-client_id="86117139794-meq950crbsqsr5a6o3iiuptqisqdurv4.apps.googleusercontent.com"
                    data-context="signup"
                    data-ux_mode="popup"
                    data-callback="handleCredentialResponse"
                    data-itp_support="true"
                    data-use_fedcm_for_prompt="true"
                  ></div>
                  <div
                    className="w-100"
                    class="g_id_signin"
                    data-type="standard"
                    data-shape="rectangular"
                    data-theme="filled_black"
                    data-text="signup_with"
                    data-size="large"
                    data-logo_alignment="left"
                  ></div>
                </div>

                <span style={{ fontSize: "1.4rem" }}>
                  Already a User?{" "}
                  <Link to="/login" className="link-light link-offset-1">
                    LOGIN
                  </Link>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
